/**
 * PR-28 — Mobile bottom safe layout manager.
 * Single source of truth for what occupies the bottom of the viewport.
 * Channels register/unregister visibility; consumers read the resolved
 * zone state and reflow events fire whenever the layout changes.
 */
import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/audit";

export type BottomChannel =
  | "bottom_nav"      // ZONA 1
  | "profile_quick"   // ZONA 2
  | "poll"            // ZONA 3
  | "post_match"
  | "halftime"
  | "participate"
  | "fab";            // ZONA 4

export interface BottomLayoutState {
  visible: Record<BottomChannel, boolean>;
  reservedSafePx: number;
}

const DEFAULTS: BottomLayoutState = {
  visible: {
    bottom_nav: false, profile_quick: false, poll: false,
    post_match: false, halftime: false, participate: false, fab: false,
  },
  reservedSafePx: 0,
};

let state: BottomLayoutState = DEFAULTS;
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const snap = () => state;
const emit = () => listeners.forEach((l) => l());

export function setBottomChannel(ch: BottomChannel, visible: boolean): void {
  if (state.visible[ch] === visible) return;
  const nextVisible = { ...state.visible, [ch]: visible };
  // ZONA 1 always reserves 72px on mobile when nav visible
  const reservedSafePx = nextVisible.bottom_nav ? 72 : 0;
  state = { visible: nextVisible, reservedSafePx };
  void trackEvent("mobile_bottom_layout_reflow", { channel: ch, visible, reservedSafePx });
  emit();
}

export function useBottomLayout(): BottomLayoutState {
  return useSyncExternalStore(subscribe, snap, snap);
}

/** Convenience: should a FAB be visible right now? */
export function useFabAllowed(): boolean {
  const s = useBottomLayout();
  // Hide FABs when poll/post-match/profile_quick occupy the bottom
  return !(s.visible.poll || s.visible.post_match || s.visible.profile_quick);
}